import { useRect } from "@reach/rect";
import {
    Tab as ReachTab,
    Tabs as ReachTabs,
    TabList,
    TabPanel,
    TabPanels,
    useTabsContext,
} from "@reach/tabs";
import "@reach/tabs/styles.css";
import classnames from "classnames";
import { useIsomorphicLayoutEffect } from "helpers/utils";
import { createContext, useContext, useRef, useState } from "react";

import sassVariables from "../../styles/variables.module.scss";
import styles from "./Tabs.module.scss";

type Props = {
    /** Alignment of row of tab switching buttons */
    tabBarAlignment?: "left" | "center";
    className?: string;
    index?: number;
    onChange?: (index: number) => void;
    defaultIndex?: number;
    tabs: React.ReactNode;
    panels: React.ReactNode;
};
const TabContext = createContext({
    setActiveRect: (_rect: DOMRect | null): void => {},
});

export function Tabs({
    tabBarAlignment = "center",
    className,
    tabs,
    panels,
    index,
    onChange,
    defaultIndex,
}: Props) {
    const [activeRect, setActiveRect] = useState<DOMRect | null>(null);

    const ref = useRef<HTMLDivElement>(null);

    const rect = useRect(ref);

    return (
        <TabContext.Provider value={{ setActiveRect }}>
            <ReachTabs
                index={index}
                onChange={onChange}
                ref={ref}
                className={className}
                defaultIndex={defaultIndex}
            >
                <TabList
                    className={classnames(styles.buttonBar, {
                        [styles.alignLeft]: tabBarAlignment === "left",
                        [styles.alignCenter]: tabBarAlignment === "center",
                        /** Follow breakpoint uses javascript so that it can depend on container width, not window width */
                        [styles.small]:
                            rect &&
                            rect.width <
                                parseInt(sassVariables.breakpointMobile),
                    })}
                >
                    {tabs}
                    <div className={styles.underline} />
                    {activeRect && rect && (
                        <div
                            className={styles.activeUnderline}
                            style={{
                                left: activeRect.left - rect.left,
                                width: activeRect.width,
                            }}
                        />
                    )}
                </TabList>

                <TabPanels>{panels}</TabPanels>
            </ReachTabs>
        </TabContext.Provider>
    );
}

export function Tab({
    index,
    className,
    ...props
}: React.ComponentProps<typeof ReachTab> & {
    index: number;
}) {
    const { selectedIndex } = useTabsContext();
    const { setActiveRect } = useContext(TabContext);
    const isSelected = selectedIndex === index;
    const ref = useRef<HTMLButtonElement>(null);

    const rect = useRect(ref);

    useIsomorphicLayoutEffect(() => {
        if (isSelected) {
            setActiveRect(rect);
        }
    }, [isSelected, rect, setActiveRect]);

    return (
        <ReachTab
            ref={ref}
            {...props}
            className={classnames([styles.button, className])}
        />
    );
}

export { TabPanel };
